import { CarrierType, NonNumericOrderDto } from '@planning/app/api'
import { isValidityExpiredByNow } from '@planning/utils/release-validity-utils'
import { EmptyIndicator } from '@tom-ui/ui'
import _ from 'lodash'
import { computed, makeObservable } from 'mobx'
import { EntityItem } from '../base/EntityItem'
import { CompanyItemStore } from '../externalDriver/CompanyItemStore'
import { IReleaseItem } from '../numericRelease/NumericReleaseItem'
import { OrderItemStore } from '../order/OrderItemStore'
import { RailVisitItemStore } from '../railVisit/RailVisitItemStore'
import { IEntity } from '../types'
import { VesselVisitItemStore } from '../vesselVisit/VesselVisitItemStore'

export interface INonNumericReleaseItem
  extends IEntity<NonNumericOrderDto>, IReleaseItem<NonNumericOrderDto> {}

export class NnrOrderItem extends EntityItem<NonNumericOrderDto> implements INonNumericReleaseItem {
  constructor(
    id: number,
    data: NonNumericOrderDto,
    private readonly orderItemStore?: OrderItemStore,
    private readonly companyItemStore?: CompanyItemStore,
    private readonly vesselVisitItemStore?: VesselVisitItemStore,
    private readonly railVisitItemStore?: RailVisitItemStore,
  ) {
    super(id, data)
    makeObservable(this, {
      orders: computed,
      operator: computed,
      canDelete: computed,
      canEdit: computed,
      carrierName: computed,
      assigned: computed,
      fulfilled: computed,
      isExpired: computed,
      transportCompany: computed,
    })
  }

  get orders() {
    return _.get(this.orderItemStore?.ordersByNnrOrderId, this.id) ?? []
  }

  get operator() {
    return _(this.companyItemStore?.elements).find(x => x.data.shortName === this.data.shippingLine)
  }

  get carrierName() {
    switch (this.data.modeOfTransport) {
      case CarrierType.Truck:
        return this.transportCompany?.data.name ?? 'TRUCK'
      case CarrierType.Train:
        return (
          _(this.railVisitItemStore?.elements).find(x => x.data.id === this.data.carrierVisitId)
            ?.data.identifier ?? EmptyIndicator
        )
      case CarrierType.Vessel:
        return (
          _(this.vesselVisitItemStore?.elements).find(x => x.data.id === this.data.carrierVisitId)
            ?.data.identifier ?? EmptyIndicator
        )
      default:
        return EmptyIndicator
    }
  }

  get transportCompany() {
    if (this.data.modeOfTransport === CarrierType.Truck && this.data.transportCompanyId) {
      return _(this.companyItemStore?.elements).find(
        x => x.data.id === this.data.transportCompanyId,
      )
    }
  }

  get canDelete() {
    return !!this.id && this.data.assigned === 0
  }

  get canEdit() {
    return !!this.id && this.fulfilled < (this.data.amount ?? 0)
  }

  get assigned() {
    return this.data.assigned
  }

  get fulfilled() {
    return this.data.fulfilled
  }

  get isExpired() {
    return isValidityExpiredByNow(this.data.validityDate)
  }
}
